import { DynamicHeader } from "./DynamicHeader";
import { Footer } from "./Footer";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { StudentToofiProvider } from "@/components/providers/StudentToofiProvider";
import { StudentToofiShell } from "@/components/ui/student-toofi-shell";
import { useEffect, Suspense } from "react";
import { capturePublicAnalytics } from "@/lib/product-analytics";
import { getPublicSurface, isPublicMarketingPath } from "@/lib/public-marketing";
import { cn } from "@/lib/utils";
import { useSiteFeatures } from "@/hooks/useSiteFeatures";
import { MobileDock } from "@/components/ui/mobile-dock";
import { AppLoadingScreen } from "@/components/shared/AppLoadingScreen";

export const DashboardLayout = () => {
    const { user, profile, isLoading } = useAuth();
    const { toofiEnabled } = useSiteFeatures();
    const location = useLocation();
    const isStudentDashboardPage =
        location.pathname === "/dashboard" ||
        location.pathname.startsWith("/dashboard/") ||
        location.pathname === "/student-dashboard" ||
        location.pathname.startsWith("/student-dashboard/");

    // MANDATORY PROFILE COMPLETION
    const isExcludedPage = ['/auth/login', '/auth/signup', '/auth/forgot-password', '/onboarding'].includes(location.pathname);

    useEffect(() => {
        const surface = getPublicSurface(location.pathname);
        if (!surface || !isPublicMarketingPath(location.pathname)) return;

        capturePublicAnalytics("public_page_viewed", {
            surface,
            pathname: location.pathname,
            authenticated: Boolean(user),
        });
    }, [location.pathname, user]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [location.pathname]);

    if (isLoading) return null;

    // If logged in but not onboarded:
    if (!isLoading && user && !profile?.onboarding_completed && !isExcludedPage && !location.pathname.startsWith('/auth')) {
        return <Navigate to="/onboarding" replace />;
    }

    // MOBILE LANDING BLOCK: Redirect mobile users away from marketing landing page immediately
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const isLandingPage = location.pathname === "/";
    if (isMobile && isLandingPage) {
        // If already logged in, redirect to appropriate authenticated zone
        if (user) {
            return <Navigate to={profile?.onboarding_completed ? "/dashboard" : "/onboarding"} replace />;
        }
        // Otherwise return null to avoid rendering any header/footer shells before redirect to auth
        return <Navigate to="/auth/login" replace />;
    }

    const isAuthPage = location.pathname === "/auth";
    const shouldHideFooter =
        isAuthPage ||
        location.pathname.startsWith('/publisher') ||
        location.pathname === '/profile' ||
        isStudentDashboardPage;

    return (
        <StudentToofiProvider>
            <div
                className={cn(
                    "min-h-screen-safe flex flex-col selection:bg-[var(--athr-brand-soft)] selection:text-[var(--athr-text-primary)]",
                    isStudentDashboardPage ? "bg-[var(--athr-canvas)]" : "page-bg",
                )}
            >
                <DynamicHeader />
                <main className="layout-padding flex-1 w-full overflow-x-hidden pb-32 pt-24 md:pb-12">
                    <div key={location.pathname} className="min-w-0">
                        <Suspense fallback={<div className="flex h-32 items-center justify-center text-sm text-slate-400">Loading...</div>}>
                            <Outlet />
                        </Suspense>
                    </div>
                </main>
                <div className="hidden md:block">
                    {!shouldHideFooter && <Footer className="browser-only" />}
                </div>
                {toofiEnabled ? <StudentToofiShell /> : null}

                {/* MOBILE NAVIGATION DOCK */}
                {isMobile && user && (
                    <div className="fixed bottom-6 left-0 right-0 z-[110] px-6 pointer-events-none mobile-navigation-dock">
                        <div className="mx-auto max-w-lg pointer-events-auto">
                            <MobileDock />
                        </div>
                    </div>
                )}
            </div>
        </StudentToofiProvider>
    );
};
